import { css, StyleSheet } from 'aphrodite'
import { Amplify } from 'aws-amplify'
import React, { useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import LayoutCol from '../../../components/Layout/LayoutCol'
import LayoutRow from '../../../components/Layout/LayoutRow'
import UIFileUpload from '../../../components/UI/UIFileUpload'
import UISubtitle from '../../../components/UI/UISubtitle'
import UITitle from '../../../components/UI/UITitle'
import config from '../../../config/aws'
import { BP_XS, MEDIA_MIN_SM } from '../../../lib/breakpoints'
import { WIDTH } from '../../../lib/helpers'
import { SPACE } from '../../../lib/spaces'
import UIButton from './../../../components/UI/UIButton'
import UIInputError from './../../../components/UI/UIInputError'

Amplify.configure({
  Auth: config.aws,
})

const styles = StyleSheet.create({
  container: {
    margin: 0,
    paddingBottom: SPACE * 10,
  },
  btnWidth: {
    width: 315,
  },
  title: {
    fontSize: 32,
    [MEDIA_MIN_SM]: {
      fontSize: 48,
    },
  },
  titleVideo: {
    [MEDIA_MIN_SM]: {
      fontSize: 40,
    },
    '@media (max-width: 1024px)': {
      fontSize: 30,
    },
    fontSize: 30,
  },
  text: {
    fontSize: 18,
    fontWeight: 400,
  },
  textBold: {
    fontWeight: 900,
  },
  spaceBtn: {
    marginTop: '8vh',
  },
  space: {
    margin: '10px 0',
  },
  containerForm: {
    display: 'flex',
    flexDirection: 'Column',
    width: '100%',
  },
  inputFile: {
    display: 'flex',
    justifyContent: 'center',
    margin: '20px 0 0',
  },
  containerList: {
    padding: 0,
    [MEDIA_MIN_SM]: {
      paddingLeft: 40,
    },
  },
  uploadVideoTitle: {
    width: '40vw',
    '@media (max-width: 480px)': {
      width: '80vw',
    },
  },
  textCenterItalic: {
    fontStyle: 'italic',
    textAlign: 'center',
  },
})

export const WorkStatus = () => {
  const { control, handleSubmit } = useForm({
    mode: 'all',
  })
  const { t } = useTranslation('translation')
  let navigate = useNavigate()
  const [error, setError] = useState('')
  const [videoMaker, setVideoMaker] = useState()
  const inputRef = useRef(null)

  const openFile = () => {
    inputRef.current.click()
  }

  const onSubmit = () => {
    if (videoMaker === undefined) {
      setError(t('common.form.errorMessage.videoEmptyError'))
    } else {
      sendVideo()
    }
  }

  const sendVideo = async () => {
    try {
      fetch(config.upload.ws_endpoint, {
        method: 'POST',
        body: JSON.stringify({ contentType: 'video/mp4' }),
        headers: {
          Authorization: `bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      })
        .then((res) => res.text())
        .then((signedUrl) => {
          fetch(signedUrl, {
            method: 'PUT',
            body: videoMaker,
            headers: {
              'Content-Type': 'video/mp4',
            },
          })
            .then(function () {
              // handle success
              localStorage.setItem('registerStatus', 'fullCompleted')
              navigate('../waiting')
            })
            .catch(function (error) {
              // handle error
              toast.error(t('error'))
              console.log(error.message)
            })
        })
        .catch(function (error) {
          // handle error
          toast.error(t('error'))
          console.log(error.message)
        })
    } catch (error) {
      toast.error(t('error'))
      console.log(error.message)
    }
  }

  return (
    <LayoutRow>
      <LayoutCol col={WIDTH <= BP_XS ? 10 : 8} dynamicStyle={styles.container}>
        <UITitle
          dynamicStyle={styles.title}
          label={t('pages.registerMaker.documents.title')}
        />
        <p className={css(styles.text)}>
          {t('pages.registerMaker.documents.instruction.paragraph1')}
          <span className={css(styles.textBold)}>
            {t('pages.registerMaker.documents.instruction.paragraph11')}
          </span>
          {t('pages.registerMaker.documents.instruction.paragraph12')}
        </p>
        <LayoutRow dynamicStyle={styles.spaceBtn} justifyStart>
          <p className={css(styles.text)}>
            {t('pages.registerMaker.documents.instruction.paragraph2')}
          </p>
        </LayoutRow>
        <LayoutRow>
          <div className={css(styles.btnWidth)}>
            <UIButton
              fullWidth
              design="primary"
              kind="button"
              label={`${t('common.button.sendFile')}`}
              onClick={() => {
                window.open(
                  'https://app.provigis.com/',
                  '_blank',
                  'noopener,noreferrer'
                )
              }}
            />
            <p className={css(styles.textCenterItalic)}>
              {t('pages.registerMaker.waiting.redirectPartner')}
            </p>
          </div>
        </LayoutRow>
        <div className={css(styles.uploadVideoTitle)}>
          <UITitle
            label={t('pages.registerMaker.onlineEvaluation.presentYourVideo')}
            dynamicStyle={styles.titleVideo}
          />
        </div>
        <UISubtitle
          label={t('pages.registerMaker.onlineEvaluation.instructionsAndHelp')}
          size="large"
        />
        <ul className={css(styles.containerList)}>
          <li className={css(styles.space)}>
            {t('pages.registerMaker.onlineEvaluation.instruction1')}
          </li>
          <li className={css(styles.space)}>
            {t('pages.registerMaker.onlineEvaluation.instruction2')}
          </li>
        </ul>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={css(styles.containerForm)}
        >
          <div className={css(styles.inputFile)}>
            <Controller
              name="video"
              control={control}
              render={({ field }) => (
                <UIFileUpload
                  {...field}
                  fileType="video"
                  documentTitle={`${t('pages.registerMaker.documents.video')}`}
                  onChange={(e) => {
                    setVideoMaker(e)
                  }}
                  ref={inputRef}
                  onClick={openFile}
                  accept="video/mp4,video/x-m4v,video/*g"
                />
              )}
            />
          </div>
          <LayoutRow>
            <div className={css(styles.btnWidth)}>
              <UIButton
                type="submit"
                fullWidth
                design="primary"
                kind="button"
                withForwardChevron
                label={`${t('common.button.submitMovie')}`}
              />
            </div>
          </LayoutRow>
          <UIInputError errorMessage={error} />
        </form>
      </LayoutCol>
    </LayoutRow>
  )
}
